<template>
  <div ref="toastRegion" id="screen-toast" class="fixed inset-x-0 z-toast m-4 md:m-6" :style="{ top: pxToRem(header.height.full) }">
    <transition-group
      appear
      aria-live="polite"
      class="pointer-within absolute inset-y-0 right-0 w-full md:max-w-91 space-y-4"
      data-test-id="screen-toast"
      enter-active-class="transform ease-out duration"
      enter-from-class="op-0 -translate-y-40 will-change-transform"
      leave-active-class="transform ease-in duration"
      leave-to-class="op-0 -translate-y-40"
      role="log"
      tag="div"
      @focusin="onFocusIn"
      @focusout="onFocusOut"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <vf-toast
        v-for="{ id, props, restoreFocus } in toasts"
        :key="id"
        v-bind="props"
        :data-toast-id="id"
        @close="closeToast(id, restoreFocus)"
      />
    </transition-group>
  </div>
</template>

<script lang="ts" setup>
const header = useHeaderStore()
const { pause, remove, resume, toasts } = useToaster()
const toastRegion = useTemplateRef('toastRegion')

let hasFocus = false
let hasHover = false

const closeToast = async (id: string | undefined, restoreFocus?: () => void) => {
  if (!id) return

  const focusedToast = document.activeElement?.closest('[data-toast-id]')
  const shouldRestoreFocus = focusedToast?.getAttribute('data-toast-id') === id
  remove(id)
  if (!shouldRestoreFocus || !restoreFocus) return

  await nextTick()
  restoreFocus()
}

const syncDismissal = () => {
  if (hasFocus || hasHover || !toasts.value.length) pause()
  else resume()
}

watch(() => toasts.value.length, async () => {
  await nextTick()
  hasFocus = toastRegion.value?.contains(document.activeElement) ?? false
  syncDismissal()
})

const onFocusIn = () => {
  hasFocus = true
  syncDismissal()
}

const onFocusOut = ({ currentTarget, relatedTarget }: FocusEvent) => {
  if (
    currentTarget instanceof HTMLElement
    && relatedTarget instanceof Node
    && currentTarget.contains(relatedTarget)
  ) {
    return
  }

  hasFocus = false
  syncDismissal()
}

const onMouseEnter = () => {
  hasHover = true
  syncDismissal()
}

const onMouseLeave = () => {
  hasHover = false
  syncDismissal()
}
</script>
